import React, { memo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import {
	Button,
	Input,
	InputType,
	SpinnerOverlay,
} from '@components/common';
import CodeForm from '@components/ModalComponents/ProfileUserForm/CodeForm';

import { useMutateSendCodeToNumberForPassword } from '@hooks/userProfile/useMutateSendCodeToNumberForPassword';
import { useMutateSentPasswordCode } from '@hooks/userProfile/useMutateSentPasswordCode';
import { useMutateUserPassword } from '@hooks/userProfile/useMutateUserPassword';
import { useQuery2FaPasswordHistory } from '@hooks/userProfile/useQuery2FaPasswordHistory';

import { ISendCodeForChangePasswordWith2Fa, IUserPasswordValues } from '@interfaces/user/userProfile';

import { EUserPasswordValues } from '@enums/userProfile';
import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { schema } from './schema';

import styles from './ChangePassword.module.scss';

interface IChangePasswordProps {
	toggleChangePasswordModal: () => void;
	is2FA: boolean;
	onClose: () => void;
}

const ChangePassword = ({
	toggleChangePasswordModal,
	is2FA,
	onClose,
}: IChangePasswordProps) => {
	const {
		register,
		handleSubmit,
		watch,
		formState: { errors },
		setValue,
		getValues,
		setError,
		trigger,
	} = useForm<IUserPasswordValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(schema),
		defaultValues: {
			[EUserPasswordValues.currentPassword]: '',
			[EUserPasswordValues.newPassword]: '',
			[EUserPasswordValues.confirmPassword]: '',
		},
	});

	const {
		isLoadingPassword,
		isPassCurrentPassword,
		isPassNewPassword,
		checkValidNewPassword,
		checkPassword,
		changePassword,
	} = useMutateUserPassword({
		setError,
		toggleChangePasswordModal,
	});

	const {
		leftSeconds,
		isLoadingHistory,
	} = useQuery2FaPasswordHistory();

	const {
		sendCodeToNumber,
		isLoadingSendCodeToNumber,
	} = useMutateSendCodeToNumberForPassword();

	const {
		sendCode,
		isLoadingSendCode,
	} = useMutateSentPasswordCode({ onSuccess: onClose });

	const onClickNext = async () => {
		const { currentPassword } = getValues();
		const isValid = await trigger(EUserPasswordValues.currentPassword);

		if (isValid) {
			checkPassword({ password: currentPassword });
		}
	};

	const onClickSave = (values: IUserPasswordValues) => {
		if (is2FA) {
			checkValidNewPassword({ password: values[EUserPasswordValues.confirmPassword] });
		} else {
			changePassword({
				oldPassword: values[EUserPasswordValues.currentPassword],
				newPassword: values[EUserPasswordValues.confirmPassword],
			});
		}
	};

	const sendCodeForm = async ({ code }: ISendCodeForChangePasswordWith2Fa) => {
		await sendCode({
			oldPassword: watch(EUserPasswordValues.currentPassword),
			newPassword: watch(EUserPasswordValues.confirmPassword),
			code,
		});
	};

	const isLoadingCode = isLoadingHistory || isLoadingSendCodeToNumber || isLoadingSendCode;

	return (
		<div className={styles.changePasswordContainer}>
			{isLoadingPassword && <SpinnerOverlay />}
			{!isPassCurrentPassword
				? <div className={styles.currentPasswordSection}>
					<div className={styles.description}>
						Please provide your current password for verification
					</div>
					<div>
						<Input
							errorMessage={errors?.currentPassword?.message}
							label='Current password'
							name={EUserPasswordValues.currentPassword}
							placeholder='Enter password'
							register={register}
							required
							setValue={setValue}
							type={InputType.password}
						/>
					</div>
				</div>
				: !isPassNewPassword
					? <div className={styles.newPasswordSection}>
						<Input
							errorMessage={errors?.newPassword?.message}
							isError={!!errors?.newPassword?.message || !!errors?.confirmPassword?.message}
							label='New Password'
							name={EUserPasswordValues.newPassword}
							placeholder='Enter new password'
							register={register}
							required
							setValue={setValue}
							tooltipContent={<div>
								The password must be between 8 and 255 characters
								<br />
								and contain:
								<ul>
									<li>Uppercase letter</li>
									<li>Lowercase letter</li>
									<li>Only Latin letters</li>
									<li>Digit (0-9)</li>
									<li>Special characters</li>
								</ul>
							</div>}
							type={InputType.password}
						/>
						<Input
							errorMessage={errors?.confirmPassword?.message}
							label='Confirm Password'
							name={EUserPasswordValues.confirmPassword}
							placeholder='Confirm password'
							register={register}
							required
							setValue={setValue}
							type={InputType.password}
						/>
					</div>
					: <CodeForm
						getCode={sendCodeToNumber}
						isLoading={isLoadingCode}
						leftSeconds={leftSeconds}
						negativeHandler={onClose}
						sendForm={sendCodeForm}
					/>
			}
			{!isPassNewPassword
				&& <div className={styles.buttons}>
					<Button
						onClick={toggleChangePasswordModal}
						text='Cancel'
						withoutBackground
					/>
					<Button
						onClick={isPassCurrentPassword ? handleSubmit(onClickSave) : onClickNext}
						text={isPassCurrentPassword && !is2FA ? 'Save' : 'Next'}
					/>
				</div>
			}
		</div>
	);
};

export default memo(ChangePassword);
